/* -------------------------------------------------------
   ActionableTradeTable — Renders an actionable table from
   actionableItems with checkboxes, dynamic totals, and
   Execute flow → POST /transactions/executeTrades.
   ------------------------------------------------------- */

import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { executeTaxHarvest } from '../../api/transactions';
import type { ActionableTable as TableType, ActionableRow } from '../../types/chatV2';

interface Props {
  table: TableType;
  username: string;
  onExecuted?: (tableId: string, rowIds: string[]) => void;
  /** When type is TAX_LOSS_HARVEST, optional replacement table to show "Buy instead" column inline. */
  replacementTable?: TableType;
  /** When type is TAX_LOSS_HARVEST (standalone), report selected tickers so replacement table can enable only those. */
  onTlhSelectionChange?: (tickers: string[]) => void;
  /** When type is REPLACEMENT_BUY (standalone), only enable dropdowns for these sold tickers. */
  enabledReplacementTickers?: Set<string>;
}

/** Default option for replacement-buy dropdown = no replacement */
const REPLACEMENT_NONE = 'NONE';

/** Keys we use for _raw aggregation (TLH / liquidation) */
const RAW_TAX_SAVINGS_KEY = 'estimatedTaxSavings_raw';
const RAW_UNREALIZED_LOSS_KEY = 'unrealizedLoss_raw';
/** Possible row.data keys for liquidation totals (backend may use different names) */
const LIQUIDATION_SELL_PROCEEDS_KEYS = ['sellProceeds_raw', 'sellProceeds', 'sell_proceeds', 'totalAmount'];
const LIQUIDATION_TAX_IMPACT_KEYS = ['estimatedTaxImpact_raw', 'estTaxImpact_raw', 'estimatedTaxImpact', 'estTaxImpact', 'taxImpact'];

function parseNumericValue(v: unknown): number | null {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string') {
    const parsed = parseFloat(v.replace(/[$,]/g, ''));
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

function getLiquidationSummary(
  rows: ActionableRow[],
  selectedIds: Set<string>,
): { totalSellProceeds: number; totalEstTaxImpact: number; fifoTaxImpact: number } {
  let totalSellProceeds = 0;
  let totalEstTaxImpact = 0;
  let fifoTaxImpact = 0;
  rows.forEach((row) => {
    if (!selectedIds.has(row.id)) return;
    const d = row.data as Record<string, unknown>;
    const payload = row.executionPayload as Record<string, unknown> | undefined;
    // Sell proceeds: from row.data or executionPayload.totalAmount
    let rowProceeds = 0;
    for (const k of LIQUIDATION_SELL_PROCEEDS_KEYS) {
      const n = parseNumericValue(d[k]);
      if (n != null) {
        rowProceeds = n;
        break;
      }
    }
    if (rowProceeds === 0 && payload?.totalAmount != null) {
      const n = parseNumericValue(payload.totalAmount);
      if (n != null) rowProceeds = n;
    }
    // Fallback: any key suggesting sell proceeds (e.g. "Sell Proceeds" from column key)
    if (rowProceeds === 0) {
      for (const [key, val] of Object.entries(d)) {
        const lower = key.toLowerCase();
        if ((lower.includes('sell') && lower.includes('proceed')) || lower === 'totalamount') {
          const n = parseNumericValue(val);
          if (n != null) {
            rowProceeds = n;
            break;
          }
        }
      }
    }
    totalSellProceeds += rowProceeds;
    // Est. tax impact: from row.data
    let rowTax = 0;
    for (const k of LIQUIDATION_TAX_IMPACT_KEYS) {
      const n = parseNumericValue(d[k]);
      if (n != null) {
        rowTax = n;
        break;
      }
    }
    if (rowTax === 0) {
      for (const [key, val] of Object.entries(d)) {
        const lower = key.toLowerCase();
        if (lower.includes('tax') && lower.includes('impact')) {
          const n = parseNumericValue(val);
          if (n != null) {
            rowTax = n;
            break;
          }
        }
      }
    }
    totalEstTaxImpact += rowTax;
    // FIFO tax impact if backend sends it
    const fifo = parseNumericValue(d.fifoTaxImpact_raw ?? d.fifoTaxImpact);
    if (fifo != null) fifoTaxImpact += fifo;
  });
  return { totalSellProceeds, totalEstTaxImpact, fifoTaxImpact };
}

/** Group REPLACEMENT_BUY rows by sold ticker for dropdown UX */
function groupReplacementRowsBySoldTicker(rows: ActionableRow[]): Map<string, ActionableRow[]> {
  const map = new Map<string, ActionableRow[]>();
  rows.forEach((row) => {
    const sold = (row.data?.soldTicker ?? (row.executionPayload as Record<string, unknown>)?.replacesTickerSymbol) as string | undefined;
    const key = sold ?? row.id;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(row);
  });
  return map;
}

function getSelectedSummary(
  rows: ActionableRow[],
  selectedIds: Set<string>,
): { count: number; taxSavings: number; unrealizedLoss: number } {
  let taxSavings = 0;
  let unrealizedLoss = 0;
  rows.forEach((row) => {
    if (!selectedIds.has(row.id)) return;
    const d = row.data as Record<string, unknown>;
    if (typeof d[RAW_TAX_SAVINGS_KEY] === 'number') taxSavings += d[RAW_TAX_SAVINGS_KEY] as number;
    if (typeof d[RAW_UNREALIZED_LOSS_KEY] === 'number') unrealizedLoss += d[RAW_UNREALIZED_LOSS_KEY] as number;
  });
  return {
    count: selectedIds.size,
    taxSavings,
    unrealizedLoss,
  };
}

/** Get ticker symbol from a TLH row for selection reporting */
function getTickerFromRow(row: ActionableRow): string {
  const ticker = row.data?.ticker ?? (row.executionPayload as Record<string, unknown>)?.tickerSymbol;
  return typeof ticker === 'string' ? ticker : '';
}

/** Keys used by dashboard to sync executed TLH / new buys (sessionStorage) */
const STORAGE_EXECUTED_TLH = 'executedTaxLossHarvesting';
const STORAGE_NEW_BUYS = 'newBuyPositions';

/**
 * Build executedTransactions and newBuyPositions in the same shape the dashboard
 * uses so sessionStorage stays in sync when executing from chat.
 */
function buildDashboardStorageFromPayloads(
  payloads: Record<string, unknown>[],
): { executedTransactions: Record<string, unknown>[]; newBuyPositions: Record<string, unknown>[] } {
  const executedTransactions = payloads
    .filter((p) => (p.type as string) === 'SELL')
    .map((p) => {
      const loss = Math.abs((p.profitLoss ?? p.unrealizedProfitLoss ?? 0) as number);
      return {
        id: p.id,
        ticker: p.tickerSymbol,
        quantity: p.quantity,
        costBasis: p.costBasis ?? p.price,
        date: p.date,
        account: p.accountName,
        type: 'SELL',
        executed: true,
        unrealizedProfitLoss: -loss,
        profitLoss: -loss,
        capitalGainTaxType: (p.capitalGainTaxType as string) ?? 'SHORT',
        matchingKey: `${p.tickerSymbol}-${p.quantity}-${p.costBasis ?? p.price}-${p.date}`,
      };
    });

  const newBuyPositions = payloads
    .filter((p) => (p.type as string) === 'BUY')
    .map((p, i) => {
      const qty = (p.quantity as number) ?? 0;
      const price = (p.costBasis ?? p.price ?? p.currentPrice) as number;
      return {
        id: (p.id as string) ?? `buy-${Date.now()}-${i}-${Math.random()}`,
        ticker: p.tickerSymbol,
        quantity: qty,
        costBasis: price ?? 0,
        date: (p.date as string) ?? new Date().toISOString().slice(0, 10),
        account: (p.accountName as string) ?? '',
        type: 'BUY',
        executed: true,
        purchaseDate: (p.date as string) ?? new Date().toISOString().slice(0, 10),
        currentPrice: (p.currentPrice ?? price) as number,
        unrealizedProfitLoss: 0,
        description: `Buy ${qty} shares of ${p.tickerSymbol}`,
        totalAmount: qty * (price ?? 0),
      };
    });

  return { executedTransactions, newBuyPositions };
}

const fmtMoney = (n: number) =>
  Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtShares = (n: number) =>
  Number(n).toLocaleString('en-US', { maximumFractionDigits: 4 });

/** Build human-readable summary of payloads for confirmation dialog (uses executionPayload totalAmount, currentPrice) */
function buildExecuteSummary(
  payloads: Record<string, unknown>[],
  replacementRows?: ActionableRow[],
): { sells: string[]; buys: string[] } {
  const sells: string[] = [];
  const buys: string[] = [];
  payloads.forEach((p) => {
    const type = p.type as string;
    const ticker = p.tickerSymbol as string;
    const qty = p.quantity as number;
    const qtyStr = qty != null ? `${fmtShares(qty)} shares` : '';
    if (type === 'SELL') {
      const currentPrice = p.currentPrice as number | undefined;
      const totalAmount = p.totalAmount as number | undefined;
      if (qtyStr && (currentPrice != null || totalAmount != null)) {
        const pricePart = currentPrice != null ? ` @ $${fmtMoney(currentPrice)}` : '';
        const totalPart = totalAmount != null ? `, total $${fmtMoney(totalAmount)}` : '';
        sells.push(`${ticker}: ${qtyStr}${pricePart}${totalPart}`);
      } else {
        sells.push(qtyStr ? `${ticker} (${qtyStr})` : ticker);
      }
    } else if (type === 'BUY') {
      const replaces = p.replacesTickerSymbol as string | undefined;
      const payloadPrice = p.currentPrice as number | undefined;
      const payloadTotal = p.totalAmount as number | undefined;
      const payloadQty = qty;
      let buyLabel: string;
      if (replacementRows) {
        const row = replacementRows.find(
          (r) =>
            (r.executionPayload as Record<string, unknown>)?.tickerSymbol === ticker &&
            (r.executionPayload as Record<string, unknown>)?.replacesTickerSymbol === replaces,
        );
        const data = row?.data as Record<string, unknown> | undefined;
        const payload = row?.executionPayload as Record<string, unknown> | undefined;
        const qtyVal = (payload?.quantity ?? data?.potentialQuantity_raw ?? data?.potentialQuantity) as number | undefined;
        const priceVal = (payload?.currentPrice ?? data?.currentPrice_raw ?? data?.currentPrice) as number | string | undefined;
        const numPrice = typeof priceVal === 'number' ? priceVal : typeof priceVal === 'string' ? parseFloat(priceVal.replace(/[$,]/g, '')) : undefined;
        const qtyNum = qtyVal != null ? Number(qtyVal) : payloadQty != null ? Number(payloadQty) : undefined;
        const totalFromPayload = (payload?.totalAmount ?? payloadTotal) as number | undefined;
        const totalAmount = totalFromPayload ?? (qtyNum != null && numPrice != null ? qtyNum * numPrice : undefined);
        const qtyDisplay = qtyNum != null ? `${fmtShares(qtyNum)} shares` : null;
        const priceDisplay = numPrice != null ? `$${fmtMoney(numPrice)}` : null;
        if (qtyDisplay && priceDisplay) {
          const totalPart = totalAmount != null ? `, total $${fmtMoney(totalAmount)}` : '';
          buyLabel = replaces
            ? `${ticker}: ${qtyDisplay} @ ${priceDisplay}${totalPart} (replacing ${replaces})`
            : `${ticker}: ${qtyDisplay} @ ${priceDisplay}${totalPart}`;
        } else {
          buyLabel = replaces ? `${ticker} (replacing ${replaces})` : (qtyStr ? `${ticker} (${qtyStr})` : ticker);
        }
      } else {
        const numPrice = payloadPrice != null ? payloadPrice : undefined;
        const qtyNum = payloadQty != null ? Number(payloadQty) : undefined;
        const totalAmount = payloadTotal ?? (qtyNum != null && numPrice != null ? qtyNum * numPrice : undefined);
        const totalPart = totalAmount != null ? `, total $${fmtMoney(totalAmount)}` : '';
        if (qtyStr && numPrice != null) {
          buyLabel = replaces ? `${ticker}: ${qtyStr} @ $${fmtMoney(numPrice)}${totalPart} (replacing ${replaces})` : `${ticker}: ${qtyStr} @ $${fmtMoney(numPrice)}${totalPart}`;
        } else {
          buyLabel = replaces ? `${ticker} (replacing ${replaces})` : (qtyStr ? `${ticker} (${qtyStr})` : ticker);
        }
      }
      buys.push(buyLabel);
    }
  });
  return { sells, buys };
}

const ActionableTradeTable: React.FC<Props> = ({
  table,
  username,
  onExecuted,
  replacementTable,
  onTlhSelectionChange,
  enabledReplacementTickers,
}) => {
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [executedRowIds, setExecutedRowIds] = useState<Set<string>>(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  const isTlhWithReplacements = table.type === 'TAX_LOSS_HARVEST' && replacementTable != null;
  const replacementOptionsByTicker = isTlhWithReplacements
    ? groupReplacementRowsBySoldTicker(replacementTable.rows)
    : null;

  // REPLACEMENT_BUY standalone: one dropdown per sold ticker
  const replacementGroups = table.type === 'REPLACEMENT_BUY' ? groupReplacementRowsBySoldTicker(table.rows) : null;
  const soldTickers = replacementGroups ? Array.from(replacementGroups.keys()) : [];
  const [replacementSelection, setReplacementSelection] = useState<Record<string, string>>({});

  // Report TLH selected tickers to parent only when no inline replacement (standalone TLH + separate replacement table)
  React.useEffect(() => {
    if (table.type !== 'TAX_LOSS_HARVEST' || replacementTable != null || !onTlhSelectionChange) return;
    const tickers = table.rows
      .filter((r) => selectedRowIds.has(r.id))
      .map(getTickerFromRow)
      .filter(Boolean);
    onTlhSelectionChange(tickers);
  }, [table.type, table.rows, selectedRowIds, onTlhSelectionChange, replacementTable]);

  const isDemo = username === 'demo';
  const selectableRows = table.rows.filter((r) => !executedRowIds.has(r.id));
  const allSelectableIds = new Set(selectableRows.map((r) => r.id));
  const selectedCount = selectedRowIds.size;

  // For REPLACEMENT_BUY: count how many *enabled* dropdowns have a replacement selected (not NONE)
  const replacementSelectedCount =
    table.type === 'REPLACEMENT_BUY'
      ? soldTickers.filter(
          (ticker) =>
            (enabledReplacementTickers != null && enabledReplacementTickers.has(ticker)) &&
            (replacementSelection[ticker] ?? REPLACEMENT_NONE) !== REPLACEMENT_NONE,
        ).length
      : 0;

  const toggleRow = useCallback((id: string) => {
    if (executedRowIds.has(id)) return;
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, [executedRowIds]);

  const toggleSelectAll = useCallback(() => {
    if (selectedCount >= allSelectableIds.size) {
      setSelectedRowIds(new Set());
    } else {
      setSelectedRowIds(new Set(allSelectableIds));
    }
  }, [selectedCount, allSelectableIds]);

  const isReplacementTickerEnabled = useCallback(
    (soldTicker: string) => enabledReplacementTickers != null && enabledReplacementTickers.has(soldTicker),
    [enabledReplacementTickers],
  );

  const setReplacementForTicker = useCallback((soldTicker: string, value: string) => {
    if (enabledReplacementTickers != null && !enabledReplacementTickers.has(soldTicker)) return;
    setReplacementSelection((prev) => ({ ...prev, [soldTicker]: value }));
  }, [enabledReplacementTickers]);

  const handleExecuteClick = useCallback(() => {
    if (table.type === 'REPLACEMENT_BUY') {
      if (replacementSelectedCount === 0) return;
    } else if (selectedCount === 0) return;
    setShowConfirm(true);
  }, [table.type, selectedCount, replacementSelectedCount]);

  const cancelConfirm = useCallback(() => {
    setShowConfirm(false);
  }, []);

  const summary = getSelectedSummary(table.rows, selectedRowIds);
  const liquidationSummary =
    table.type === 'LIQUIDATION' ? getLiquidationSummary(table.rows, selectedRowIds) : null;
  const allSelected = allSelectableIds.size > 0 && selectedCount === allSelectableIds.size;
  const isReplacementTable = table.type === 'REPLACEMENT_BUY';
  const someSelected = isTlhWithReplacements
    ? selectedCount > 0
    : isReplacementTable
      ? replacementSelectedCount > 0
      : selectedCount > 0;
  const executeCount = isTlhWithReplacements ? selectedCount : isReplacementTable ? replacementSelectedCount : selectedCount;

  /** Payloads that would be sent on execute (for combined TLH: SELLs + chosen BUYs) */
  const getPayloadsForExecute = useCallback((): Record<string, unknown>[] => {
    if (isTlhWithReplacements && replacementTable) {
      const sellPayloads = table.rows
        .filter((r) => selectedRowIds.has(r.id))
        .map((r) => r.executionPayload as Record<string, unknown>);
      const buyPayloads: Record<string, unknown>[] = [];
      table.rows.forEach((r) => {
        if (!selectedRowIds.has(r.id)) return;
        const ticker = getTickerFromRow(r);
        const rowId = replacementSelection[ticker] ?? REPLACEMENT_NONE;
        if (rowId === REPLACEMENT_NONE || rowId === '') return;
        const replRow = replacementTable.rows.find((x) => x.id === rowId);
        if (replRow) buyPayloads.push(replRow.executionPayload as Record<string, unknown>);
      });
      return [...sellPayloads, ...buyPayloads];
    }
    if (isReplacementTable) {
      return soldTickers
        .filter((ticker) => {
          if (enabledReplacementTickers != null && !enabledReplacementTickers.has(ticker)) return false;
          const v = replacementSelection[ticker] ?? REPLACEMENT_NONE;
          return v !== REPLACEMENT_NONE && v !== '';
        })
        .map((ticker) => {
          const row = table.rows.find((r) => r.id === replacementSelection[ticker]);
          return row!.executionPayload as Record<string, unknown>;
        });
    }
    return table.rows
      .filter((r) => selectedRowIds.has(r.id))
      .map((r) => r.executionPayload as Record<string, unknown>);
  }, [
    isTlhWithReplacements,
    isReplacementTable,
    replacementTable,
    table.rows,
    table.type,
    selectedRowIds,
    replacementSelection,
    soldTickers,
    enabledReplacementTickers,
  ]);

  const confirmExecute = useCallback(async () => {
    const payloads = getPayloadsForExecute();
    if (!isDemo || payloads.length === 0) {
      setShowConfirm(false);
      return;
    }

    setIsExecuting(true);

    try {
      await executeTaxHarvest(payloads);

      if (isTlhWithReplacements || table.type === 'TAX_LOSS_HARVEST') {
        const executedIds = Array.from(selectedRowIds);
        setExecutedRowIds((prev) => new Set(Array.from(prev).concat(executedIds)));
        setSelectedRowIds(new Set());
        onExecuted?.(table.id, executedIds);
      } else if (isReplacementTable) {
        const executedIds = soldTickers
          .filter((ticker) => (replacementSelection[ticker] ?? REPLACEMENT_NONE) !== REPLACEMENT_NONE)
          .map((ticker) => replacementSelection[ticker]!);
        onExecuted?.(table.id, executedIds);
      }
      setShowConfirm(false);

      // Mirror dashboard: update sessionStorage so dashboard reflects chat executions (demo)
      const { executedTransactions: newExec, newBuyPositions: newBuys } =
        buildDashboardStorageFromPayloads(payloads);
      if (newExec.length > 0 || newBuys.length > 0) {
        try {
          const existingExec = JSON.parse(
            typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STORAGE_EXECUTED_TLH) ?? '[]' : '[]',
          );
          const existingBuys = JSON.parse(
            typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STORAGE_NEW_BUYS) ?? '[]' : '[]',
          );
          sessionStorage.setItem(
            STORAGE_EXECUTED_TLH,
            JSON.stringify([...existingExec, ...newExec]),
          );
          sessionStorage.setItem(
            STORAGE_NEW_BUYS,
            JSON.stringify([...existingBuys, ...newBuys]),
          );
        } catch (e) {
          console.warn('SessionStorage sync after execute:', e);
        }
      }

      toast.success(
        `Executed ${payloads.length} trade${payloads.length > 1 ? 's' : ''}.`,
        { position: 'top-right', autoClose: 4000 },
      );

      window.dispatchEvent(
        new CustomEvent('taxLossHarvestingExecuted', {
          detail: { executedPayloads: payloads, executedTransactions: newExec, newBuyPositions: newBuys },
        }),
      );
    } catch (err) {
      console.error('Execute trades error:', err);
      toast.error('Failed to execute. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsExecuting(false);
    }
  }, [isDemo, isTlhWithReplacements, isReplacementTable, table.type, table.id, selectedRowIds, replacementSelection, soldTickers, getPayloadsForExecute, onExecuted]);

  return (
    <div className="cv2-actionable-table">
      <div className="cv2-at-label">{table.label}</div>

      <div className="cv2-table-wrap cv2-at-table-wrap">
        <table className="cv2-at-table">
          <thead>
            <tr>
              {!isReplacementTable && (
                <th className="cv2-at-th-checkbox">
                  {selectableRows.length > 0 && (
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all"
                      className="cv2-at-checkbox"
                    />
                  )}
                </th>
              )}
              {table.columns.map((col) => (
                <th key={col.key} className="cv2-at-th">
                  {col.header}
                </th>
              ))}
              {isTlhWithReplacements && (
                <th key="buy-instead" className="cv2-at-th">
                  Buy instead
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {isReplacementTable && replacementGroups ? (
              soldTickers.map((soldTicker) => {
                const options = replacementGroups.get(soldTicker) ?? [];
                const firstReason = options[0]?.data?.reason;
                const enabled = isReplacementTickerEnabled(soldTicker);
                const selectedValue = enabled ? (replacementSelection[soldTicker] ?? REPLACEMENT_NONE) : REPLACEMENT_NONE;
                return (
                  <tr
                    key={soldTicker}
                    className={`cv2-at-row ${!enabled ? 'cv2-at-row--replacement-disabled' : ''}`}
                  >
                    <td className="cv2-at-td">{soldTicker}</td>
                    <td className="cv2-at-td cv2-at-td-dropdown">
                      <select
                        value={selectedValue}
                        onChange={(e) => setReplacementForTicker(soldTicker, e.target.value)}
                        className="cv2-at-select"
                        aria-label={`Replacement for ${soldTicker}`}
                        disabled={!enabled}
                        title={enabled ? undefined : 'Select this ticker in Tax Loss Harvesting Candidates above to choose a replacement'}
                      >
                        <option value={REPLACEMENT_NONE}>NONE</option>
                        {options.map((row) => {
                          const ticker = (row.data?.replacementTicker ?? (row.executionPayload as Record<string, unknown>)?.tickerSymbol) as string;
                          return (
                            <option key={row.id} value={row.id}>
                              {ticker}
                            </option>
                          );
                        })}
                      </select>
                    </td>
                    <td className="cv2-at-td">{firstReason != null ? String(firstReason) : '—'}</td>
                  </tr>
                );
              })
            ) : (
              table.rows.map((row) => {
                const executed = executedRowIds.has(row.id);
                const selected = selectedRowIds.has(row.id);
                const ticker = getTickerFromRow(row);
                const replacementOptions = isTlhWithReplacements && replacementOptionsByTicker ? replacementOptionsByTicker.get(ticker) ?? [] : [];
                const replacementValue = replacementSelection[ticker] ?? REPLACEMENT_NONE;
                return (
                  <tr
                    key={row.id}
                    className={`cv2-at-row ${selected ? 'cv2-at-row--selected' : ''} ${executed ? 'cv2-at-row--executed' : ''}`}
                  >
                    <td className="cv2-at-td-checkbox">
                      {executed ? (
                        <span className="cv2-at-executed-mark" aria-hidden="true">
                          &#10003;
                        </span>
                      ) : (
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleRow(row.id)}
                          aria-label={`Select row ${row.id}`}
                          className="cv2-at-checkbox"
                        />
                      )}
                    </td>
                    {table.columns.map((col) => {
                      let val = row.data[col.key];
                      if ((val === undefined || val === null || val === '') && /account/i.test(col.key)) {
                        val = ((row.data as Record<string, unknown>).accountName
                          ?? (row.data as Record<string, unknown>).account
                          ?? (row.executionPayload as Record<string, unknown>)?.accountName
                          ?? (row.executionPayload as Record<string, unknown>)?.account) as string | number;
                      }
                      const display = val !== undefined && val !== null && val !== '' ? String(val) : '—';
                      return (
                        <td key={col.key} className="cv2-at-td">
                          {display}
                        </td>
                      );
                    })}
                    {isTlhWithReplacements && (
                      <td className="cv2-at-td cv2-at-td-dropdown">
                        <select
                          value={replacementValue}
                          onChange={(e) => setReplacementForTicker(ticker, e.target.value)}
                          className="cv2-at-select"
                          aria-label={`Replacement for ${ticker}`}
                        >
                          <option value={REPLACEMENT_NONE}>NONE</option>
                          {replacementOptions.map((replRow) => {
                            const replTicker = (replRow.data?.replacementTicker ?? (replRow.executionPayload as Record<string, unknown>)?.tickerSymbol) as string;
                            return (
                              <option key={replRow.id} value={replRow.id}>
                                {replTicker}
                              </option>
                            );
                          })}
                        </select>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {table.rows.some((r) => r.warnings?.length) ? (
        <div className="cv2-at-warnings">
          {table.rows.flatMap((row) =>
            (row.warnings ?? []).map((w, i) => (
              <div key={`${row.id}-w-${i}`} className="cv2-at-warning">
                {row.data?.ticker ? `${String(row.data.ticker)}: ` : ''}{w}
              </div>
            )),
          )}
        </div>
      ) : null}

      {someSelected && (
        <div className="cv2-at-summary">
          {table.type === 'LIQUIDATION' && liquidationSummary ? (
            <>
              <span className="cv2-at-summary-item">
                Total sell proceeds: ${Math.abs(liquidationSummary.totalSellProceeds).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              {liquidationSummary.fifoTaxImpact !== 0 && (
                <span className="cv2-at-summary-item">
                  FIFO tax impact: ${Math.abs(liquidationSummary.fifoTaxImpact).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              )}
              <span className="cv2-at-summary-item">
                Total est. tax impact: ${Math.abs(liquidationSummary.totalEstTaxImpact).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </>
          ) : (
            <>
              {summary.taxSavings !== 0 && (
                <span className="cv2-at-summary-item">
                  Est. tax savings: ${Math.abs(summary.taxSavings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              )}
              {summary.unrealizedLoss !== 0 && (
                <span className="cv2-at-summary-item">
                  Harvestable loss: ${Math.abs(summary.unrealizedLoss).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              )}
            </>
          )}
        </div>
      )}

      {(selectableRows.length > 0 || (isReplacementTable && soldTickers.length > 0)) && (
        <div className="cv2-at-execute-bar">
          {isDemo ? (
            <button
              type="button"
              className="cv2-at-execute-btn"
              onClick={handleExecuteClick}
              disabled={!someSelected || isExecuting}
              aria-label={someSelected ? `Execute ${executeCount} selected` : isReplacementTable ? 'Choose replacements to execute' : 'Select rows to execute'}
            >
              {isExecuting
                ? 'Executing…'
                : someSelected
                  ? `Execute (${executeCount} selected)`
                  : isReplacementTable
                    ? 'Execute replacement buys'
                    : isTlhWithReplacements
                      ? 'Execute trades'
                      : 'Execute selected'}
            </button>
          ) : (
            <button type="button" className="cv2-at-execute-btn cv2-at-execute-btn--disabled" disabled>
              Coming Soon
            </button>
          )}
        </div>
      )}

      {showConfirm && (() => {
        const payloads = getPayloadsForExecute();
        const replacementRows = isTlhWithReplacements && replacementTable ? replacementTable.rows : undefined;
        const { sells, buys } = buildExecuteSummary(payloads, replacementRows);
        return (
        <div className="cv2-at-confirm-backdrop" role="dialog" aria-modal="true" aria-labelledby="cv2-at-confirm-title">
          <div className="cv2-at-confirm">
            <h3 id="cv2-at-confirm-title" className="cv2-at-confirm-title">
              Confirm trades
            </h3>
            <p className="cv2-at-confirm-intro">
              The following trades will be executed:
            </p>
            <div className="cv2-at-confirm-summary-block">
              {sells.length > 0 && (
                <div className="cv2-at-confirm-section">
                  <strong>Sell ({sells.length})</strong>
                  <ul className="cv2-at-confirm-list">
                    {sells.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {buys.length > 0 && (
                <div className="cv2-at-confirm-section">
                  <strong>Buy ({buys.length})</strong>
                  <ul className="cv2-at-confirm-list">
                    {buys.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {summary.taxSavings !== 0 && sells.length > 0 && (
              <p className="cv2-at-confirm-summary">
                Estimated tax savings: ${Math.abs(summary.taxSavings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            )}
            <div className="cv2-at-confirm-actions">
              <button type="button" className="cv2-at-confirm-cancel" onClick={cancelConfirm}>
                Cancel
              </button>
              <button
                type="button"
                className="cv2-at-confirm-ok"
                onClick={confirmExecute}
                disabled={isExecuting}
              >
                {isExecuting ? 'Executing…' : 'Execute'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default ActionableTradeTable;
